import React, { useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { fetchCustomers } from 'actions/action-filters';
import { fetchCustomerClinics, fetchSmsTemplatesForCustomer } from 'actions/action-lookups';
import { Controller, useForm } from 'react-hook-form';
import { nameOfFactory } from 'utils/types-util';
import { ReactSelect } from 'components/form/field/react_select';
import { useTypedSelector } from 'hooks/use-typed-selector';
import { NumberOption } from 'interfaces/forms/types';
import { UserPermissions } from 'interfaces/user/Permissions';
import { UserUtils } from 'utils/user-utils';

interface ICustomerDropdownProps {
  selectedCustomerId: number;
  setSelectedCustomerId: (customerId: any) => void;
  isLabelOn?: boolean;
  isSelectedOption?: boolean;
  requiredPermission?: UserPermissions;
  isMulti?: boolean;
  isDisabled?: boolean;
}

interface IFormFields {
  customerId: {
    label: string;
    value: number;
  };
}

const getFormFields = nameOfFactory<IFormFields>();

export const CustomerDropdown: React.FC<ICustomerDropdownProps> = (
  props: ICustomerDropdownProps,
): JSX.Element => {
  const dispatch = useDispatch();
  const customers = useTypedSelector(state => state.filters.customers);
  const selectedCustomerId = useTypedSelector(state => state.filters.selectedCustomerId);

  const form = useForm<any>();

  const useIsMulti = props.isMulti || false;
  const useIsDisabled = props.isDisabled || false;

  useEffect(() => {
    dispatch(fetchCustomers());
    dispatch(fetchCustomerClinics());
    dispatch(fetchSmsTemplatesForCustomer(selectedCustomerId));
  }, [fetchCustomers, fetchCustomerClinics]);

  const allCustomers = useMemo(() => {
    const options = [...customers]
      .sort((a, b) => a.name.localeCompare(b.name))
      .map<NumberOption>(x => {
        const isDisabled =
          props.requiredPermission != null &&
          !UserUtils.userIsPermitted(props.requiredPermission, x.id);
        return {
          label: isDisabled ? `${x.name} (No permission)` : x.name,
          value: x.id,
          disabled: isDisabled,
        };
      });

    if (props.isSelectedOption) {
      options.push({ label: 'Select Customer', value: 0 });
    }

    return options;
  }, [customers, props.requiredPermission, props.isSelectedOption]);

  const calcValue = (val: any) => {
    const defaultAll = [{ label: 'All', value: -1 }];

    if (!Array.isArray(val) && !useIsMulti) {
      return [val];
    }
    if (!Array.isArray(val) && useIsMulti) {
      return defaultAll;
    }
    if (!useIsDisabled) {
      const filteredVal = val.filter((v: { label: string; value: number }) => v.value !== -1);

      if (useIsMulti && val.length === 0) {
        return defaultAll;
      }
      return filteredVal;
    }
    return null;
  };

  const calcLabel = () => {
    if (props.isLabelOn) {
      return useIsMulti ? 'Customers' : 'Customer';
    }
    return undefined;
  };

  return (
    <Controller
      defaultValue={
        allCustomers.find(customer => customer.value === props.selectedCustomerId) ||
        allCustomers[0]
      }
      control={form.control}
      name={getFormFields('customerId')}
      render={(ctrlProps: any) => {
        return (
          <ReactSelect
            value={calcValue(ctrlProps?.field?.value)}
            fields={allCustomers}
            isMulti={useIsMulti}
            isDisabled={useIsDisabled}
            label={calcLabel()}
            isOptionDisabled={(option: NumberOption) => option.disabled === true}
            handleOnChange={(value: any) => {
              const filteredVal = useIsMulti
                ? value
                    .filter((v: { label: string; value: number }) => v.value !== -1)
                    .map((v: { label: string; value: number }) => v.value)
                : value.value;

              form.setValue(getFormFields('customerId'), value);
              props.setSelectedCustomerId(filteredVal);
            }}
          />
        );
      }}
    />
  );
};

export default CustomerDropdown;
